import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const Wrapped2025 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    fetchWrappedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWrappedData = async () => {
    try {
      const response = await fetchWithAuth('/api/analytics/wrapped-2025-eligibility');
      if (!response.ok) throw new Error('Failed to fetch wrapped data');
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading 2025 Wrapped analysis...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!data) return null;

  const { summary, topFoodLoggers, topCalorieUsers, topStreakUsers, foodLogDistribution, calorieDistribution, streakDistribution } = data;

  const eligibilityPercentage = summary.totalUsersIn2025 > 0 
    ? ((Math.min(summary.usersWithMin10Foods, summary.usersWithMin1000Calories, summary.usersWithMin5DayStreak) / summary.totalUsersIn2025) * 100).toFixed(1)
    : 0;

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
        🎊 2025 Wrapped Feature Analysis
      </h1>
      <p style={{ color: '#666', marginBottom: '24px', fontSize: '16px' }}>
        Analyzing user activity to determine if a "2025 Wrapped" feature is viable
      </p>

      {/* Summary Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <div className="stat-card" style={{ background: '#eff6ff' }}>
          <div className="stat-label">Users with 10+ Food Logs</div>
          <div className="stat-value">{summary.usersWithMin10Foods}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {((summary.usersWithMin10Foods / summary.totalUsersIn2025) * 100).toFixed(1)}% of active users
          </div>
        </div>

        <div className="stat-card" style={{ background: '#f0fdf4' }}>
          <div className="stat-label">Users with 1000+ Calories</div>
          <div className="stat-value">{summary.usersWithMin1000Calories}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {((summary.usersWithMin1000Calories / summary.totalUsersIn2025) * 100).toFixed(1)}% of active users
          </div>
        </div>

        <div className="stat-card" style={{ background: '#fef3c7' }}>
          <div className="stat-label">Users with 5+ Day Streak</div>
          <div className="stat-value">{summary.usersWithMin5DayStreak}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {((summary.usersWithMin5DayStreak / summary.totalUsersIn2025) * 100).toFixed(1)}% of active users
          </div>
        </div>

        <div className="stat-card" style={{ background: '#f3e8ff' }}>
          <div className="stat-label">Total Active Users</div>
          <div className="stat-value">{summary.totalUsersIn2025}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            In 2025
          </div>
        </div>
      </div>

      {/* Recommendation Card */}
      <div className="card" style={{ 
        background: eligibilityPercentage > 20 ? '#d1fae5' : eligibilityPercentage > 10 ? '#fef3c7' : '#fee2e2',
        border: `2px solid ${eligibilityPercentage > 20 ? '#10b981' : eligibilityPercentage > 10 ? '#f59e0b' : '#ef4444'}`,
        marginBottom: '24px'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {eligibilityPercentage > 20 ? '✅' : eligibilityPercentage > 10 ? '⚠️' : '❌'}
          Feature Recommendation
        </h2>
        <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '12px' }}>
          {eligibilityPercentage > 20 ? (
            <strong style={{ color: '#065f46' }}>
              ✨ Recommended! About {eligibilityPercentage}% of users meet all minimum criteria. 
              This is a good base for a "2025 Wrapped" feature.
            </strong>
          ) : eligibilityPercentage > 10 ? (
            <strong style={{ color: '#92400e' }}>
              ⚠️ Consider carefully. Only {eligibilityPercentage}% meet all criteria. 
              You might want to lower the thresholds or target power users only.
            </strong>
          ) : (
            <strong style={{ color: '#991b1b' }}>
              ❌ Not recommended yet. Only {eligibilityPercentage}% meet all criteria. 
              Consider building engagement first or significantly lowering thresholds.
            </strong>
          )}
        </p>
        <div style={{ fontSize: '14px', color: '#666' }}>
          <strong>Suggestion:</strong> You could create tiered "Wrapped" experiences:
          <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
            <li>🥇 Premium: {summary.usersWithMin10Foods} users (10+ logs, 1000+ cals, 5+ streak)</li>
            <li>🥈 Standard: Lower thresholds (5+ logs, 500+ cals, 3+ streak)</li>
            <li>🥉 Beginner: Anyone with activity (1+ log)</li>
          </ul>
        </div>
      </div>

      {/* Distribution Cards */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h3 className="card-title">Food Log Distribution</h3>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <strong>10-49 logs</strong>
                </td>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>
                  {foodLogDistribution.min10} users
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <strong>50-99 logs</strong>
                </td>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>
                  {foodLogDistribution.min50} users
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <strong>100-199 logs</strong>
                </td>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>
                  {foodLogDistribution.min100} users
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0' }}>
                  <strong>200+ logs</strong> 🏆
                </td>
                <td style={{ padding: '12px 0', textAlign: 'right' }}>
                  {foodLogDistribution.min200} users
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <h3 className="card-title">Calorie Distribution</h3>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <strong>1K-10K kcal</strong>
                </td>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>
                  {calorieDistribution.min1000} users
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <strong>10K-50K kcal</strong>
                </td>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>
                  {calorieDistribution.min10000} users
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <strong>50K-100K kcal</strong>
                </td>
                <td style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>
                  {calorieDistribution.min50000} users
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0' }}>
                  <strong>100K+ kcal</strong> 🏆
                </td>
                <td style={{ padding: '12px 0', textAlign: 'right' }}>
                  {calorieDistribution.min100000} users
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 className="card-title">Streak Distribution</h3>
        <div className="grid grid-4">
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>
              {streakDistribution.min5}
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              5-9 days
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>
              {streakDistribution.min10}
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              10-29 days
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>
              {streakDistribution.min30}
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              30-59 days
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#8b5cf6' }}>
              {streakDistribution.min60}
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              60+ days 🔥
            </div>
          </div>
        </div>
      </div>

      {/* Top Performers */}
      <div className="card">
        <h2 className="card-title">🏆 Top Food Loggers in 2025</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>User</th>
                <th>Email</th>
                <th>Food Logs</th>
                <th>Total Calories</th>
                <th>Avg Calories/Log</th>
              </tr>
            </thead>
            <tbody>
              {topFoodLoggers.map((user, index) => (
                <tr key={user.user_id}>
                  <td>
                    <span style={{ fontSize: '18px' }}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                    </span>
                  </td>
                  <td>
                    <strong>{user.user_name || `User ${user.user_id}`}</strong>
                  </td>
                  <td style={{ fontSize: '13px', color: '#666' }}>
                    {user.user_email || 'N/A'}
                  </td>
                  <td>{user.food_count} logs</td>
                  <td>{Math.round(user.total_calories).toLocaleString()} kcal</td>
                  <td>{Math.round(user.total_calories / user.food_count)} kcal</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Wrapped2025;

